/** @module csi/def/ViewStoreBuilder */
define([
	"dojo/_base/declare",
	"dojo/store/Memory",
	"dstore/Memory",
	"dstore/Tree",
	"csi",
	"csi/_base/underscore",
	"csi/core/ExtendedPath",
	"csi/def",
	"csi/locale",
	"csi/reflection",
	"csi/reflectionHints",
	"csi/service/nodeService",
	"csi/state/rootStore",
	"csi/state/datasetup",
	"csi/state/viewUtils",
	"csi/store/Cache",
	"csi/view",
	"csi/xml"
], function(declare, Memory, dMemory, dTree, csi, _, ExtendedPath, def, locale, reflection, reflectionHints, {createClientNodesForPath}, {sessionStore},
	{dataSetupStore, DataSetupItem, DataSetupGroup, DataSetupSubsection, DataSetupSection, DataSetupSectionId},
	{composeViewIdWithSubtype}, Cache, csiView, xml) {

	/**
	 * @typedef {string} ViewKey
	 * Key for a ViewStoreItem
	 */

	/**
	 * @typedef {Object} ViewStoreItem
	 * @property {boolean} canDisable - indicates if view can be disabled
	 * @property {boolean} canHide - indicates if view can be hidden
	 * @property {ViewStoreItem[]} children - child views
	 * @property {boolean} editable - indicates if view is configured to be editable
	 * @property {ExtendedPath} extendedPath - path to view relative to root class
	 * @property {boolean} hasChildren - indicates if view has child views
	 * @property {boolean} hasName - indicates if view has name
	 * @property {ViewKey} id - unique identifier for store item
	 * @property {string} name - localized name of view
	 * @property {string} parent - id of parent item (optional)
	 * @property {string} viewId - view identifier
	 * @property {string} viewType - type of view
	 * @property {boolean} visible - indicates if view is configured to be visible
	 */

	/**
	 * A function returning true or false indicating if the User Default is applicable
	 * @callback viewStoreBuilderFilter
	 * @param {ViewStoreItem} item
	 * @return {boolean}
	 */

	/**
	 * Parameters that are available for ViewStoreBuilder
	 * @typedef {Object} ViewStoreBuilderParams
	 * @property {boolean} disabled - indicates if view preferences can be changed 
	 * @property {viewStoreBuilderFilter} filter - function to filter items in the store 
	 * @property {boolean} omitRoot - indicates if root view should be omitted from store
	 * @property {Node} rolePrefs - role preferences node to use to determine visibility and editability of views
	 */

	const HTML = "Html";
	const TABS = "Tabs";

	var _isRole;
	var _disabled;
	var _filter;
	var _hidden;
	var _items;
	var _keys;
	var _readOnly;
	var _siteHidden;
	var _siteReadOnly;
	var _typeKey;

	/**
	 * Update extended path with given path; however, if url is specified, so the view is global and not relative to starting class
	 * @param {ExtendedPath} ep
	 * @param {string} path
	 * @param {string} [url]
	 * @return {ExtendedPath}
	 */
	function _updateExtendedPath(ep, path, url) {
		if (url || !ep) {
			return null;
		}

		try {
			if (path) {
				ep = ep.clone();
				ep.addPath(path);
			}

			return ep;
		} catch (ex) {
			return null;
		}
	}

	/**
	 * Traverse view to create store items
	 * @param {string} type - type owning view
	 * @param {string} viewId - view id
	 * @param {ViewStoreBuilderParams} [params] - optional parameters used to construct the store
	 * @return {ViewStoreItem[]}
	 */
	function _process(type, viewId, params) {
		const site = sessionStore.sitePreferences;
		const ep = new ExtendedPath(type);
		var rc = reflection.getClass(type);

		_siteHidden = csi.hashArray(site.HiddenViews, null, Boolean);
		_siteReadOnly = csi.hashArray(site.ReadOnlyViews, null, Boolean);
		_typeKey = type;
		_keys = {};
		_items = [];

		if (rc.isMaster()) {
			// for master classes, the root view id is on its revision class
			ep.addRelationStep(reflection.ATTR_LATEST_REVISION);
			rc = ep.getEndClass();
		} else if (rc.isRevision()) {
			// for revision classes, use master type as key to keep compatibility with older releases (when __Host__ view was on Master rather than Revision)
			_typeKey = rc.getMasterClass().getType();
		}

		// process parameters
		params = params || {};
		_disabled = params.disabled || false;
		_filter = params.filter;

		const rolePrefs = params.rolePrefs;
		if (rolePrefs) {
			_hidden = csi.hashArray(rolePrefs.HiddenViews, null, Boolean);
			_readOnly = csi.hashArray(rolePrefs.ReadOnlyViews, null, Boolean);
			_isRole = true;
		} else {
			_hidden = _siteHidden;
			_readOnly = _siteReadOnly;
			_isRole = false;
		}

		// traverse view tree
		let rootItem;

		if (rc.getType() == reflection.TYPE_DATA_SETUP) {
			rootItem = _loadDataSetupStore(viewId, dataSetupStore.sections.filter(x => x.id !== DataSetupSectionId.SERVICES));
		} else if (rc.getType() == reflection.TYPE_QUEUES_SERVICES) {
			rootItem = _loadDataSetupStore(viewId, dataSetupStore.sections.filter(x => x.id === DataSetupSectionId.SERVICES));
		} else {
			rootItem = _traverse(rc, ep, viewId, null, params.omitRoot);
		}

		// if not including root item in store, then reset parent of all its children
		if (params.omitRoot) {
			_.each(rootItem.children, function(item) {
				item.parent = null;
			});
		}

		// a new view is added under a Tabs view that was previously hidden, 
		// we need to update the parent's visibility to true since the new view is visible by default
		_updateViewParentsVisibility(rootItem);

		// cleanup variables
		const items = _items;
		_items = null;
		_keys = null;

		return items;
	}

	/**
	 * Generate the single item based on the object
	 * @param {DataSetupSection | DataSetupSubsection | DataSetupGroup | DataSetupItem} dataItem 
	 * @param {Object} rootItem
	 * @return {Object}
	 */
	function _generateDataItem(dataItem, rootItem) {
		let {
			id,
			name,
			viewId
		} = dataItem;

		// in the case of DataSetupItem, keep the key pattern the same as before, which is <viewId> + "|"+ <nodeType> + "|" + context
		// other than that, use the unique id as the key, which is group/section/subsection ID + "|" +context
		let key;

		if (dataItem instanceof DataSetupItem) {
			const {subtypeName, viewOwnerType} = dataItem;
			const viewIdWithSubtype = composeViewIdWithSubtype(viewId, subtypeName);
			key = id = def.composeViewKey(viewIdWithSubtype, viewOwnerType, _typeKey);
		} else {
			key = id = def.composeViewKey(id, _typeKey);
		}

		const item = {
			id,
			name,
			parent: rootItem.id,
			hasName: true,
			viewId: viewId || id, // for the section/subsection/group case, use the id as the viewId
			visible: !_hidden[key] && !_siteHidden[key],
			editable: !_readOnly[key] && !_siteReadOnly[key],
			canHide: (!_isRole || !_siteHidden[key]) && !_disabled,
			canDisable: (!_isRole || !_siteReadOnly[key]) && !_disabled,
			hasChildren: false
		};

		_items.push(item);
		return item;
	}

	/**
	 * Iterate the store to build the tree
	 * @param {DataSetupSection | DataSetupSubsection | DataSetupGroup } dataItem 
	 * @param {Object} parent 
	 * @return {Object}
	 */
	function _loopStore(dataItem, parent) {
		let children = [];
		const item = _generateDataItem(dataItem, parent);

		const _generateLeafItemForGroup = (group, parent, children) => {
			group.items.forEach((item) => {
				children.push(_generateDataItem(item, parent));
			});
		}

		if (dataItem instanceof DataSetupSection) {
			_.each(dataItem.children, (child) => {
				if (child.id) {
					children.push(_loopStore(child, item));
				} else {
					// item follows directly
					_generateLeafItemForGroup(child, item, children);
				}
			});
		} else if (dataItem instanceof DataSetupSubsection) {
			_.each(dataItem.groups, (child) => {
				if (child.id) {
					children.push(_loopStore(child, item));
				} else {
					// item follows directly
					_generateLeafItemForGroup(child, item, children);
				}
			});
		} else if (dataItem instanceof DataSetupGroup) {
			_generateLeafItemForGroup(dataItem, item, children);
		}

		if (children && children.length) {
			item.children = children;
			item.hasChildren = true;
		}

		return item;
	}

	/**
	 *  Load the store of the Data Setup module
	 * @param {string} id  id of the view
	 * @param {DataSetupSection[]} sections sections to be rendered
	 * @return {Object}
	 */
	function _loadDataSetupStore(id, sections) {
		const key = def.composeViewKey(id, _typeKey);
		const name = locale.resource("ViewConfigurationRoot");

		let rootItem = {
			id: key,
			name,
			parent: null,
			hasName: true,
			viewId: id,
			visible: !_hidden[key] && !_siteHidden[key],
			editable: !_readOnly[key] && !_siteReadOnly[key],
			canHide: false, // always keep disabled
			canDisable: (!_isRole || !_siteReadOnly[key]) && !_disabled
		};

		rootItem.children = sections.map((dataItem) => _loopStore(dataItem, rootItem));
		rootItem.hasChildren = true;
		_items.push(rootItem);

		return rootItem;
	}

	/**
	 * Traverse view to update its parents visibility
	 * @param {ViewStoreItem|null} view
	 * @param {ViewStoreItem|null} [parent]
	 */
	function _updateViewParentsVisibility(view, parent) {
		if (view) {
			view.children?.forEach((item) => {
				_updateViewParentsVisibility(item, view);
			});

			if (parent) {
				parent.visible ||= view.visible;
			}
		}
	}

	/**
	 * Traverse view to find child views
	 * @param {ReflectionClass} rc
	 * @param {ExtendedPath} ep
	 * @param {string} viewId
	 * @param {ViewStoreItem} [parent]
	 * @param {boolean} [bSkip]
	 * @return {ViewStoreItem|null}
	 */
	function _traverse(rc, ep, viewId, parent, bSkip) {
		var defView = def.getClassViewDef(rc, viewId);
		if (!defView) {
			return null;
		}

		// if the same view is encountered more than once, then skip
		var key = def.composeViewKey(defView.m_id, defView.m_idType, _typeKey);
		if (_keys[key]) {
			return null;
		}

		_keys[key] = true;

		// create item and look for child views based on view type
		var type = rc.getType();
		var typeView = defView.getType();
		var rcTarget = defView.getPageClass(rc);
		var bRootHomeView = !parent && (reflectionHints.isTypeOfLandingPage(type) || (viewId == def.getHostViewId(type)));
		const isTabView = (typeView == TABS);
		const isCompositeView = (typeView == HTML) && !defView.isSlim();
		var name;
		var children;

		if (parent) {
			name = locale.getResolver({typeContext: type, bUseDefault: false}).getTypeView(type, viewId);
		} else {
			name = locale.resource("ViewConfigurationRoot");
		}

		const visible = !_hidden[key] && !_siteHidden[key];

		var item = {
			extendedPath: ep,
			id: key,
			name: (name == null) ? viewId : name,
			parent: parent ? parent.id : null,
			hasName: (name != null),
			viewId,
			viewType: typeView,
			visible,
			editable: !_readOnly[key] && !_siteReadOnly[key] && !defView.isReadOnly(),
			canHide: (!_isRole || !_siteHidden[key]) && !bRootHomeView && !_disabled,
			canDisable: (!_isRole || !_siteReadOnly[key]) && !defView.isReadOnly() && !_disabled
		};

		if (!isTabView && !isCompositeView) {
			item.target = rcTarget ? locale.type(rcTarget.getType()) : "";
		}

		if (_filter && !_filter(item)) {
			return null;
		}

		if (isTabView) {
			children = csi.transform(defView.tabs, function(tab) {
				const childPath = _updateExtendedPath(ep, tab.m_path, tab.m_url);
				return _traverse(tab.getRootNodeClass(rc), childPath, tab.m_idView, item);
			}, this);
		} else if (isCompositeView) {
			children = csi.transform(defView.getViewRefs(), function(vr) {
				const childPath = _updateExtendedPath(ep, vr.m_path, vr.m_url);
				return _traverse(vr.getReferencedClass(rc), childPath, vr.m_id, item);
			}, this);
		} else if (rcTarget && defView.idContentView) {
			// handle switchboard views - extended path is null for switchboard content views as they are not relative 
			// to root class; rather, they are relative to result node of parent view
			var content = _traverse(rcTarget, null, defView.idContentView, item, null, true);
			if (content && content.children && !content.target) {
				// make child views of content view appear as children of switchboard view - unless it is also switchboard (i.e. Artwork)
				_.each(content.children, function(child) {
					child.parent = key;
				});
				// if content is container then remove it, don't show empty container as it's children are already moved to switchboard view level
				if ((content.viewType === TABS) || (content.viewType === HTML)) {
					_items.pop();
				}

				item.children = content.children;
			} else {
				item.children = [content];
			}
		}

		if (children && children.length) {
			item.children = children;
			item.hasChildren = true;
		} else {
			item.hasChildren = false;
		}

		if (!bSkip) {
			_items.push(item);
		}

		return item;
	}

	/**
	 * Return Memory store of views referenced by given type and view id
	 * @param {string} type - type owning view
	 * @param {string} viewId - root view id
	 * @param {ViewStoreBuilderParams} [params] - optional parameters used to construct the store
	 * @return {Memory}
	 */
	function getStore(type, viewId, params) {
		return new Memory({
			data: _process(type, viewId, params),

			getChildren: function(object, options) {
				return this.query({parent: object.id}, options);
			},

			mayHaveChildren: function(object) {
				return object.children;
			}
		});
	}

	/**
	 * Return Memory store (for dgrid 1.x) of views referenced by given type and view id
	 * @param {string} type - type owning view
	 * @param {string} viewId - root view id
	 * @param {ViewStoreBuilderParams} [params] - optional parameters used to construct the store
	 * @return {dstore/Memory}
	 */
	function getDStore(type, viewId, params) {
		return new declare([dMemory, dTree])({
			data: _process(type, viewId, params)
		});
	}

	/**
	 * Return custom view container key for the given view key; if custom views are not supported, then return null
	 * @param {ViewKey} viewKey
	 * @param {ExtendedPath} viewPath
	 * @param {string} [subtypeUrl] - subtype of node that could influence custom view key 
	 * @return {string|null}
	 */
	function getCustomViewContainerKey(viewKey, viewPath, subtypeUrl) {
		// create dummy root node for view
		const cache = new Cache();
		const startNode = createClientNodesForPath(viewPath, cache);

		// in this case, the assumption is that subtype should be applied to node at start of path
		// as it should be the domain object
		if (xml.isValidURL(subtypeUrl)) {
			xml.nodeCreateClientAttr(startNode, reflection.ATTR_SUBTYPE, xml.ATTR_REF, subtypeUrl);
		}

		// create view
		const node = cache.getPathNode(startNode, viewPath.toString());
		const defView = getViewDefinition(viewKey, viewPath);
		const view = csiView.newView(defView, node, {cacheView: cache}, true);

		return view.supportsCustomViews() ? view.getViewPreferenceKey() : null;
	}

	/**
	 * Return View definition for given store item
	 * @param {ViewKey} viewKey
	 * @param {ExtendedPath} viewPath
	 * @return {View}
	 */
	function getViewDefinition(viewKey, viewPath) {
		const keyObj = def.decomposeViewKey(viewKey);
		return def.getClassViewDef(viewPath.getEndClass(), keyObj.id);
	}

	return {
		getDStore,
		getStore,
		getCustomViewContainerKey,
		getViewDefinition,
	};
});
